// ===== 认证状态管理 (Auth State — reactive 共享，无 Pinia) =====
// 沿用项目 reactive 共享状态模式，与 tenant.ts 风格一致
// v3: 对接后端 Sa-Token 登录 API，并从真实 org/role API 恢复 RBAC 权限

import { reactive } from 'vue'
import type { OrgEmployee, SysRole } from '../types/models'
import { loginApi, logoutApi, getUserInfoApi } from '../api/auth'
import type { SysUser, LoginDTO } from '../api/auth'
import { getCurrentOrgEmployee } from '../api/org'
import { getRoleList } from '../api/role'
import { clearVisitedTabs } from './visitedTabs'
import { clearConfiguredMenus, loadConfiguredMenus } from './menu'

// ===== Auth State =====
interface AuthState {
  /** 当前登录用户（后端 SysUser） */
  sysUser: SysUser | null
  /** 当前登录用户（前端 OrgEmployee，权限体系用） */
  currentUser: OrgEmployee | null
  /** 当前用户的角色列表 */
  roles: SysRole[]
  /** 扁平化去重后的权限标识合集 */
  permissions: string[]
  /** 是否已登录 */
  isLoggedIn: boolean
  /** Sa-Token 令牌 */
  token: string | null
}

export const authState = reactive<AuthState>({
  sysUser: null,
  currentUser: null,
  roles: [],
  permissions: [],
  isLoggedIn: false,
  token: null,
})

const normalizeId = (value: unknown) => String(value ?? '')
const normalizeRoleCode = (value: unknown) => String(value ?? '').trim().toUpperCase()

const getUserDisplayName = (user: SysUser | null) => {
  if (!user) return ''
  return (user as any).realName || (user as any).real_name || user.username || ''
}

const buildLegacyAdminRole = (): SysRole => ({
  id: 'ADMIN',
  role_code: 'ADMIN',
  role_name: '系统管理员',
  role_type: 'builtin',
  description: '系统内置管理员角色',
  permissions: ['*'],
  user_count: 1,
  status: 1,
  is_super: true,
})

// ===== 设置权限体系（真实 API 数据）=====
function setupPermissions(employee: OrgEmployee | null, allRoles: SysRole[], user: SysUser | null) {
  authState.currentUser = employee

  const activeRoles = (allRoles || []).filter(r => r.status !== 0)
  const userRoleIds = ((employee as any)?.role_ids ?? (employee as any)?.roleIds ?? [])
    .map((id: unknown) => normalizeId(id))
    .filter(Boolean)

  let matchedRoles = activeRoles.filter(r => userRoleIds.includes(normalizeId(r.id)))

  // 兼容 0.2.x 阶段 sys_user.role 字段；正式 0.3 会完全由 sys_user_role 接管。
  const legacyRoleCode = normalizeRoleCode((user as any)?.role)
  if (matchedRoles.length === 0 && legacyRoleCode) {
    matchedRoles = activeRoles.filter(r => normalizeRoleCode((r as any).role_code) === legacyRoleCode)
  }
  if (matchedRoles.length === 0 && legacyRoleCode === 'ADMIN') {
    matchedRoles = [buildLegacyAdminRole()]
  }

  authState.roles = matchedRoles

  // 扁平化去重权限标识
  const permSet = new Set<string>()
  authState.roles.forEach(r => {
    r.permissions?.forEach(p => permSet.add(p))
  })
  authState.permissions = [...permSet]
}

async function loadPermissionContext(user: SysUser) {
  const [employeeRes, rolesRes] = await Promise.allSettled([
    getCurrentOrgEmployee(),
    getRoleList(),
  ])

  const employee = employeeRes.status === 'fulfilled' ? (employeeRes.value.data || null) : null
  const roles = rolesRes.status === 'fulfilled' && Array.isArray(rolesRes.value.data) ? rolesRes.value.data : []
  setupPermissions(employee, roles, user)
  await loadConfiguredMenus()
}

// ===== 登录（调用后端 API）=====
/**
 * 调用后端登录 API，获取 Token，并从真实 API 设置权限体系
 * @param dto 登录表单
 */
export async function login(dto: LoginDTO): Promise<void> {
  clearVisitedTabs()
  const res = await loginApi(dto)
  const { token, user } = res.data

  // 1. 持久化 Token
  authState.token = token
  authState.sysUser = user
  authState.isLoggedIn = true
  localStorage.setItem('token', token)
  localStorage.setItem('isLoggedIn', 'true')
  localStorage.setItem('username', getUserDisplayName(user))
  localStorage.setItem('loginTime', new Date().toISOString())

  await loadPermissionContext(user)
}

// ===== 快速切换角色登录（仅开发环境兼容入口）=====
/** @deprecated 0.2.9 起正式登录不再使用 Mock 快捷登录。 */
export function quickLogin(employee: OrgEmployee, allRoles: SysRole[]) {
  if (!(import.meta as any).env?.DEV) {
    throw new Error('Quick login is disabled outside development mode')
  }
  clearVisitedTabs()
  clearConfiguredMenus()
  // 沿用旧逻辑：不调后端，仅在前端设置登录态
  authState.currentUser = employee
  authState.isLoggedIn = true
  authState.sysUser = null
  authState.token = null
  setupPermissions(employee, allRoles, null)

  localStorage.setItem('isLoggedIn', 'true')
  localStorage.setItem('currentUserId', employee.id)
  localStorage.setItem('username', employee.name)
  localStorage.setItem('loginTime', new Date().toISOString())
}

// ===== 登出 =====
export async function logout() {
  // 尝试调后端登出（忽略错误）
  try {
    if (authState.token) {
      await logoutApi()
    }
  } catch (_) { /* ignore */ }

  authState.sysUser = null
  authState.currentUser = null
  authState.roles = []
  authState.permissions = []
  authState.isLoggedIn = false
  authState.token = null
  clearConfiguredMenus()
  clearVisitedTabs()
  localStorage.removeItem('token')
  localStorage.removeItem('isLoggedIn')
  localStorage.removeItem('currentUserId')
  localStorage.removeItem('username')
  localStorage.removeItem('loginTime')
  localStorage.removeItem('rememberMe')
}

// ===== 从 localStorage 恢复登录态 (应用启动时调用) =====
/**
 * 尝试从持久化存储恢复登录态
 * - 如果有 Token → 调后端 /auth/info 验证
 */
export async function restoreAuth(): Promise<boolean> {
  const isLoggedIn = localStorage.getItem('isLoggedIn')
  if (isLoggedIn !== 'true') return false

  const token = localStorage.getItem('token')

  if (token) {
    authState.token = token
    try {
      const res = await getUserInfoApi()
      authState.sysUser = res.data
      authState.isLoggedIn = true
      await loadPermissionContext(res.data)
      return true
    } catch (_) {
      // Token 过期或后端不可达 → 清除登录态
      localStorage.removeItem('token')
      localStorage.removeItem('isLoggedIn')
      localStorage.removeItem('currentUserId')
      clearVisitedTabs()
      clearConfiguredMenus()
      authState.token = null
      authState.isLoggedIn = false
      return false
    }
  }

  localStorage.removeItem('currentUserId')
  localStorage.removeItem('isLoggedIn')
  clearVisitedTabs()
  clearConfiguredMenus()
  return false
}

// ===== 核心鉴权函数 =====
/**
 * 检查当前用户是否拥有指定权限（OR 关系：任一命中即通过）
 */
export function hasPermission(requiredPerms: string | string[]): boolean {
  // Super Admin 拥有一切
  if (authState.permissions.includes('*')) return true

  const perms = Array.isArray(requiredPerms) ? requiredPerms : [requiredPerms]

  return perms.some(p => {
    // 精确匹配
    if (authState.permissions.includes(p)) return true
    // 用户持有 module:* 时匹配 module:xxx
    const [mod] = p.split(':')
    if (authState.permissions.includes(`${mod}:*`)) return true
    return false
  })
}

// ===== Super Admin 判断 =====
export function isSuperAdmin(): boolean {
  return authState.permissions.includes('*')
    || authState.roles.some(r => r.is_super === true)
}

// ===== 获取当前用户的角色名称列表 =====
export function getCurrentRoleNames(): string[] {
  return authState.roles.map(r => r.role_name)
}

// ===== 获取当前用户的首要角色名称（用于 Header / toast 提示）=====
export function getPrimaryRoleName(): string {
  if (authState.roles.length === 0) return '未分配角色'
  const superRole = authState.roles.find(r => r.is_super)
  return superRole ? superRole.role_name : authState.roles[0].role_name
}
